import Highcharts, { Chart, DataLabelsOverflowValue, Point } from 'highcharts';
import { defaultOptions, legendOptions } from '../shared/Config';

import { BuildChartOptions } from '../Factories/types';
import buildLegend from '../Factories/buildLegend';

export interface ComparisonBarChartValue {
  leftValue: number;
  rightValue?: number;
  color?: Highcharts.ColorString | Highcharts.GradientColorObject | Highcharts.PatternObject;
  comparisonLeftValue?: number;
  comparisonRightValue?: number;
  comparisonColor?: Highcharts.ColorString | Highcharts.GradientColorObject | Highcharts.PatternObject;
}

export interface ComparisonBarChartSeries {
  name: string;
  color?: Highcharts.ColorString | Highcharts.GradientColorObject | Highcharts.PatternObject;
  data: ComparisonBarChartValue[];
}

export type Row = {
  isClickable?: boolean;
  label: string;
  onClick?: () => void;
  rowHeight: number;
  barHeight?: number;
};

export type formatterType = (value: number) => string;

export interface BuildComparisonBarChartOptions extends BuildChartOptions {
  leftTitle: string;
  rightTitle: string;
  tooltipFormatter: (row: any) => string;
  leftFormatter: formatterType;
  rightFormatter: formatterType;
  rows: Row[];
  series: ComparisonBarChartSeries[];
  setHoveredRowIndex: (rowIndex: number | null) => void;
  comparisonTitle: string;
  comparisonSelectedTitle: string;
  changeLabelsColor: boolean;
}

export interface ExtendedAxis extends Highcharts.Axis {
  dataMin: number;
  dataMax: number;
}

type allowedPointers = 'pointer' | 'not-allowed' | 'default';

const leftChartColor = '#6480c3';
const rightChartPositiveColor = '#59b75b';
const rightChartNegativeColor = '#ba302a';

const leftDataColor = '#2b4c9b';
const rightDataPositiveColor = '#167922';
const rightDataNegativeColor = '#bc1c0e';

const leftComparisonChartColor = '#c0c5c8';
const leftComparisonDataColor = '#414752';

const DEFAULT_BAR_HEIGHT = 20;
const INACTIVE_OPACITY = 0.2;

const DEFAULT_LEFT_WIDTH = '42%';
const DEFAULT_RIGHT_WIDTH = '42%';
const DEFAULT_RIGHT_LEFT = '58%';

const labelStyles = {
  fontSize: '12px',
  color: '#414752',
  fontWeight: 'normal',
  fontFamily: 'Lato',
  textOverflow: 'ellipsis',
  maxWidth: '160px',
};

const titleStyles = {
  ...labelStyles,
  fontSize: '14px',
};

const dataLabelsStyles = {
  ...titleStyles,
  fontWeight: 'bold',
  textOutline: 'none',
};

const zeroPlotLines = [
  {
    value: 0,
    width: 1,
    color: '#bbc0c9',
    zIndex: '2',
  },
];

const getCursor = (isClickable: boolean, hasClickAction: boolean): allowedPointers => {
  if (isClickable) {
    return 'pointer';
  }
  if (!isClickable && hasClickAction) {
    return 'not-allowed';
  }
  return 'default';
};

// chart height considers relative group and point padding
export const getChartHeight = (rows: Row[], seriesCount: number): number =>
  rows.reduce(
    (sum, row) => sum + Math.max(row.rowHeight, (seriesCount * (row.barHeight ?? DEFAULT_BAR_HEIGHT) * 100) / 60),
    150,
  );

const setDataLabelOpacity = (point: Point, opacity: number) => {
  // yet another undocumented feature...
  // we have to set the data labels' opacity because it is not done automatically
  // when setting the state for the point
  const dl = (point as any).dataLabel;
  if (dl && dl.attr) {
    dl.attr({ opacity });
  }
};

let redrawChartTimeout: NodeJS.Timeout;

export const setRowSelection = (
  chart: Chart | undefined,
  rowNum: number,
  setHoveredRowIndex: (rowIndex: number | null) => void,
): void => {
  clearTimeout(redrawChartTimeout);

  if (chart?.series) {
    chart.series.forEach((s) => {
      s.setState();
      s.data.forEach((point, pointIndex) => {
        const state = rowNum === pointIndex ? 'normal' : 'inactive';
        point.setState(state, true);

        setDataLabelOpacity(point, state === 'normal' ? 1 : INACTIVE_OPACITY);
      });
    });

    setHoveredRowIndex(rowNum);
  }

  // No chart.redraw() here, otherwise our changes are lost!
};

export const removeRowSelection = (
  chart: Chart | undefined,
  setHoveredRowIndex: (rowIndex: number | null) => void,
): void => {
  clearTimeout(redrawChartTimeout);

  // have to use global here to disambiguate from window.setTimeout()
  redrawChartTimeout = global.setTimeout(() => {
    if (chart?.series) {
      chart.series.forEach((s) => {
        s.data.forEach((point) => {
          setDataLabelOpacity(point, 1);
        });
      });

      // redraw needed to reset states to normal
      chart.redraw();

      setHoveredRowIndex(null);
    }
  }, 200);
};

const buildComparisonBarChartOptions = (buildChartOptions: BuildComparisonBarChartOptions): Highcharts.Options => {
  const {
    leftTitle,
    rightTitle,
    leftFormatter,
    rightFormatter,
    tooltipFormatter,
    series,
    rows,
    setHoveredRowIndex,
    comparisonTitle,
    comparisonSelectedTitle,
    changeLabelsColor,
  } = buildChartOptions;

  // const baseOptions = buildOptions(buildChartOptions, defaultOptions);
  const baseOptions = defaultOptions;
  const legend = buildLegend({
    ...legendOptions,
    ...baseOptions.legend,
    ...buildChartOptions.legendOptions,
    enabled: series.length > 1,
  });

  const categories = rows.map((row, i) => {
    return `<div data-test-id="chart-label" style="cursor:${getCursor(
      Boolean(row.isClickable),
      Boolean(row.onClick),
    )};" id="x-label-${i}">${row.label}</div>`;
  });

  const hasChanges = series.some((s) =>
    s.data.some((d) => d.rightValue !== undefined || d.comparisonRightValue !== undefined),
  );

  const parseNumberValue = (value: string | number): number => {
    const parsedNumber = typeof value === 'number' ? value : parseFloat(value);
    return isNaN(parsedNumber) ? 0 : parsedNumber;
  };

  return {
    ...baseOptions,
    chart: {
      ...baseOptions?.chart,
      height: getChartHeight(rows, series.length),
      events: {
        load: function () {
          const yAxisOfRightChart = this.yAxis[1] as ExtendedAxis;
          const maxValueOfYAxisOfRightChart = yAxisOfRightChart.dataMax;
          if (maxValueOfYAxisOfRightChart > 0) {
            const newMaxValueOfYAxisOfRightChart = maxValueOfYAxisOfRightChart + maxValueOfYAxisOfRightChart * 0.3;
            this.yAxis[1].update({
              max: newMaxValueOfYAxisOfRightChart,
            });
          }

          // enable hover and click on axis labels
          const chartElement = document.getElementById(this.container.id);
          if (chartElement && rows.length) {
            rows.forEach((row, index) => {
              const el = chartElement.querySelector(`#x-label-${index}`);
              el?.addEventListener('click', () => row.isClickable && row.onClick && row.onClick());
              el?.addEventListener('mouseenter', () => setRowSelection(this, index, setHoveredRowIndex));
              el?.addEventListener('mouseleave', () => removeRowSelection(this, setHoveredRowIndex));
            });
          }

          this.series.forEach((s) => {
            s.data.forEach((d, i) => {
              // What we are doing here is not officially documented but highly performant
              // because we do not have to set new cursors and re-render the chart while hovering.
              if ((d.options as any).cursorEnabled && rows.length > i) {
                const g = (d as any).graphic;
                if (g && g.attr) {
                  g.attr({ cursor: getCursor(Boolean(rows[i].isClickable), Boolean(rows[i].onClick)) });
                }
              }
            });

            // This is also undocumented but suggested by Highcharts support to enable the correct
            // highlighting of linked series when hovering the legend items
            const anySeries = s as any;
            if (anySeries.legendGroup) {
              Highcharts.addEvent(anySeries.legendGroup.element, 'mouseover', () => {
                // Internal method
                // Can be also implemented manually as `series.setState('inactive');`
                (this.pointer as any).applyInactiveState([anySeries.points[0]]);
              });
              Highcharts.addEvent(anySeries.legendGroup.element, 'mouseout', () => {
                this.series.forEach((s2) => s2.setState());
              });
            }
          });
        },
      },
    },
    plotOptions: {
      bar: {
        groupPadding: 0.1,
        maxPointWidth: (rows.length > 0 ? rows[0].barHeight : undefined) || DEFAULT_BAR_HEIGHT,
        minPointLength: 3,
      },
      series: {
        animation: false,
      },
    },
    legend,
    tooltip: {
      useHTML: true,
      padding: 5,
      backgroundColor: '#fff',
      borderColor: '#bbc0c9',
      formatter: function (this: Highcharts.TooltipFormatterContextObject) {
        return tooltipFormatter(rows[this.point.index]);
      },
      stickOnContact: true,
      shadow: false,
      outside: true,
      style: {
        zIndex: '40',
      },
    },
    xAxis: [
      {
        // axis for the left chart
        lineColor: '#bbc0c9',
        tickColor: '#bbc0c9',
        categories,
        labels: {
          useHTML: true,
          style: {
            ...labelStyles,
            textAlign: 'right',
            overflow: 'visible',
          },
        },
        alternateGridColor: '#f5f6f7',
      },
      {
        // axis for the right chart
        minorTicks: false,
        lineWidth: 0,
        lineColor: '#bbc0c9',
        tickColor: '#fff',
        categories,
        labels: {
          enabled: false,
        },
        alternateGridColor: '#f5f6f7',
      },
    ],
    yAxis: [
      {
        // axis for the left chart
        width: hasChanges ? DEFAULT_LEFT_WIDTH : '100%',
        tickAmount: 7,
        labels: {
          formatter: (point) => {
            const value = parseNumberValue(point.value);
            return leftFormatter(value);
          },
          overflow: 'allow',
          style: {
            ...labelStyles,
            color: '#6f7582',
          },
        },
        title: {
          text: undefined,
        },
        plotLines: zeroPlotLines,
      },
      {
        // axis for the right chart
        width: DEFAULT_RIGHT_WIDTH,
        left: DEFAULT_RIGHT_LEFT,
        tickAmount: 7,
        startOnTick: false,
        tickLength: 20,
        offset: 0,
        labels: {
          formatter: (point) => {
            const value = parseNumberValue(point.value);
            return rightFormatter(value);
          },
          overflow: 'allow',
          style: {
            ...labelStyles,
            color: '#6f7582',
          },
        },
        title: {
          text: undefined,
        },
        plotLines: zeroPlotLines,
      },
      {
        // additional axis to make the left title appear above the chart
        width: hasChanges ? DEFAULT_LEFT_WIDTH : '100%',
        labels: {
          enabled: false,
        },
        opposite: true,
        title: {
          text: leftTitle,
          style: titleStyles,
        },
      },
      {
        // additional axis to make the right title appear above the chart
        width: DEFAULT_RIGHT_WIDTH,
        left: DEFAULT_RIGHT_LEFT,
        offset: 0,
        labels: {
          enabled: false,
        },
        opposite: true,
        title: {
          text: hasChanges ? rightTitle : undefined,
          style: titleStyles,
        },
      },
    ],
    series: series?.flatMap((s, index) => {
      return [
        {
          id: `${index}-first-left`,
          name: s.name,
          xAxis: 0,
          yAxis: 0,
          type: 'bar',
          point: {
            events: {
              click: (x) => {
                const row = rows[x.point.index];
                return row.onClick && row.onClick();
              },
              mouseOver: (x) =>
                setRowSelection(
                  (x.target as any as Point).series.chart,
                  (x.target as any as Point).index,
                  setHoveredRowIndex,
                ),
              mouseOut: (x) => removeRowSelection((x.target as any as Point).series.chart, setHoveredRowIndex),
            },
          },
          dataLabels: [
            {
              enabled: true,
              allowOverlap: true,
              inside: false,
              overflow: 'allow' as DataLabelsOverflowValue,
              padding: 8,
              crop: false,
              style: dataLabelsStyles,
              formatter: function (this: Highcharts.PointLabelObject) {
                return leftFormatter(this.y ?? 0);
              },
            },
          ],
          color: s.color ?? leftChartColor,
          data: [],
        },
        {
          id: `${index}-second-left`,
          name: s.name,
          xAxis: 0,
          yAxis: 0,
          type: 'bar',
          point: {
            events: {
              click: (x) => {
                const row = rows[x.point.index];
                return row.onClick && row.onClick();
              },
              mouseOver: (x) =>
                setRowSelection(
                  (x.target as any as Point).series.chart,
                  (x.target as any as Point).index,
                  setHoveredRowIndex,
                ),
              mouseOut: (x) => removeRowSelection((x.target as any as Point).series.chart, setHoveredRowIndex),
            },
          },
          dataLabels: [
            {
              enabled: true,
              allowOverlap: true,
              inside: false,
              overflow: 'allow' as DataLabelsOverflowValue,
              padding: 8,
              crop: false,
              style: dataLabelsStyles,
              formatter: function (this: Highcharts.PointLabelObject) {
                return leftFormatter(this.y ?? 0);
              },
            },
            {
              enabled: true,
              inside: true,
              useHTML: true,
              crop: false,
              overflow: 'allow' as DataLabelsOverflowValue,
              align: 'left',
              // eslint-disable-next-line max-len
              format: `<div style="min-width: 125px; text-align: right; color: ${'#414752'};">${comparisonTitle}</div>`,
              x: -146,
              style: labelStyles,
            },
          ],
          color: s.color ?? leftComparisonChartColor,
          data: s.data?.map((d) => ({
            y: d.comparisonLeftValue,
            cursorEnabled: true,
            color: d.comparisonColor ?? s.color ?? leftComparisonChartColor,
            dataLabels: {
              color: changeLabelsColor ? d.comparisonColor : leftComparisonDataColor,
            },
          })),
        },
        {
          id: `${index}-third-left`,
          name: s.name,
          xAxis: 0,
          yAxis: 0,
          type: 'bar',
          point: {
            events: {
              click: (x) => {
                const row = rows[x.point.index];
                return row.onClick && row.onClick();
              },
              mouseOver: (x) =>
                setRowSelection(
                  (x.target as any as Point).series.chart,
                  (x.target as any as Point).index,
                  setHoveredRowIndex,
                ),
              mouseOut: (x) => removeRowSelection((x.target as any as Point).series.chart, setHoveredRowIndex),
            },
          },
          dataLabels: [
            {
              enabled: true,
              allowOverlap: true,
              inside: false,
              overflow: 'allow' as DataLabelsOverflowValue,
              padding: 8,
              crop: false,
              style: dataLabelsStyles,
              formatter: function (this: Highcharts.PointLabelObject) {
                return this.y ? leftFormatter(this.y ?? 0) : null;
              },
            },
            {
              enabled: true,
              inside: true,
              useHTML: true,
              crop: false,
              overflow: 'allow' as DataLabelsOverflowValue,
              align: 'left',
              // eslint-disable-next-line max-len
              format: `<div style="width: 125px; text-align: right; color: ${'#414752'}; text-overflow: ellipsis; overflow: hidden; white-space: no-wrap;">${comparisonSelectedTitle}</div>`,
              x: -146,
              style: labelStyles,
            },
          ],
          color: s.color ?? leftChartColor,
          data: s.data?.map((d) => ({
            y: d.leftValue,
            cursorEnabled: true,
            color: d.color ?? s.color ?? leftChartColor,
            dataLabels: {
              color: changeLabelsColor ? leftComparisonDataColor : d.color ?? s.color ?? leftDataColor,
            },
          })),
        },
        {
          id: `${index}-first-right`,
          linkedTo: `${index}-first-left`, // enables proper legend hover
          xAxis: 1,
          yAxis: 1,
          type: 'bar',
          point: {
            events: {
              click: (x) => {
                const row = rows[x.point.index];
                return row.onClick && row.onClick();
              },
              mouseOver: (x) =>
                setRowSelection(
                  (x.target as any as Point).series.chart,
                  (x.target as any as Point).index,
                  setHoveredRowIndex,
                ),
              mouseOut: (x) => removeRowSelection((x.target as any as Point).series.chart, setHoveredRowIndex),
            },
          },
          dataLabels: [
            {
              enabled: true,
              allowOverlap: true,
              inside: false,
              overflow: 'allow' as DataLabelsOverflowValue,
              padding: 8,
              crop: false,
              style: dataLabelsStyles,
              formatter: function (this: Highcharts.PointLabelObject) {
                return rightFormatter(this.y ?? 0);
              },
            },
          ],
          color: rightChartPositiveColor,
          negativeColor: rightChartNegativeColor,
          data: [],
        },
        {
          id: `${index}-second-right`,
          linkedTo: `${index}-second-left`, // enables proper legend hover
          xAxis: 1,
          yAxis: 1,
          type: 'bar',
          point: {
            events: {
              click: (x) => {
                const row = rows[x.point.index];
                return row.onClick && row.onClick();
              },
              mouseOver: (x) =>
                setRowSelection(
                  (x.target as any as Point).series.chart,
                  (x.target as any as Point).index,
                  setHoveredRowIndex,
                ),
              mouseOut: (x) => removeRowSelection((x.target as any as Point).series.chart, setHoveredRowIndex),
            },
          },
          dataLabels: [
            {
              enabled: true,
              allowOverlap: true,
              inside: false,
              overflow: 'allow' as DataLabelsOverflowValue,
              padding: 8,
              crop: false,
              style: dataLabelsStyles,
              formatter: function (this: Highcharts.PointLabelObject) {
                return rightFormatter(this.y ?? 0);
              },
            },
          ],
          color: rightChartPositiveColor,
          negativeColor: rightChartNegativeColor,
          data: s.data?.map((d) => {
            const value = d.comparisonRightValue ?? 0;
            return {
              y: d.comparisonRightValue,
              borderColor: value < 0 ? undefined : rightChartPositiveColor,
              cursorEnabled: true,
              dataLabels: {
                color: value < 0 ? rightDataNegativeColor : rightDataPositiveColor,
              },
            };
          }),
        },
        {
          id: `${index}-third-right`,
          linkedTo: `${index}-third-left`, // enables proper legend hover
          xAxis: 1,
          yAxis: 1,
          type: 'bar',
          point: {
            events: {
              click: (x) => {
                const row = rows[x.point.index];
                return row.onClick && row.onClick();
              },
              mouseOver: (x) =>
                setRowSelection(
                  (x.target as any as Point).series.chart,
                  (x.target as any as Point).index,
                  setHoveredRowIndex,
                ),
              mouseOut: (x) => removeRowSelection((x.target as any as Point).series.chart, setHoveredRowIndex),
            },
          },
          dataLabels: [
            {
              enabled: true,
              allowOverlap: true,
              inside: false,
              overflow: 'allow' as DataLabelsOverflowValue,
              padding: 8,
              crop: false,
              style: dataLabelsStyles,
              formatter: function (this: Highcharts.PointLabelObject) {
                return rightFormatter(this.y ?? 0);
              },
            },
          ],
          color: rightChartPositiveColor,
          negativeColor: rightChartNegativeColor,
          data: s.data?.map((d) => {
            const value = d.rightValue ?? 0;
            return {
              y: d.rightValue,
              borderColor: value < 0 ? undefined : rightChartPositiveColor,
              cursorEnabled: true,
              dataLabels: {
                color: value < 0 ? rightDataNegativeColor : rightDataPositiveColor,
              },
            };
          }),
        },
      ];
    }),
  };
};

export default buildComparisonBarChartOptions;
